import React, {
  FC,
  Fragment,
  PropsWithChildren,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  Period,
  Product,
  SubscriptionService,
} from "@service/subscription/subscription.service";
import { RevenuecatSubscriptionService } from "@service/subscription/revenuecat-subscription.service";
import {
  Button,
  Stack,
  Table,
  tableCellClasses,
  TableRowProps,
  Tooltip,
  Typography,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@store/store";
import { useTranslation } from "react-i18next";
import Loader from "@components/Loader/Loader";
import TableRow from "@mui/material/TableRow";
import TableBody from "@mui/material/TableBody";
import TableCell, { TableCellProps } from "@mui/material/TableCell";
import BillingPeriodSwitch from "@components/SubscriptionSelect/BillingPeriodSwitch";
import {
  useGetPlansQuery,
  useGetPlanSubscriptionDetailsQuery,
} from "@store/planApi";
import { Plan } from "@models/plan";
import { PlanSubscriptionDetails } from "@models/plan-subscription-details";
import { isExpired } from "@utils/subscription.util";
import ConfirmDialog from "@components/ConfirmDialog/ConfirmDialog";
import { setMessage } from "@store/snackbarSlice";
import styled from '@emotion/styled';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

const Image = styled.img`
  width: auto;
  height: 50px;
`;

function isFreeTrial(
  subscription: PlanSubscriptionDetails | undefined,
  product: Product,
): boolean {
  if (!subscription) {
    return false;
  }
  return (
    subscription.isTrial &&
    subscription.plan.name === product.id &&
    !isExpired(subscription)
  );
}

const SubscriptionItem: FC<{
  product: Product;
  onSubscribe: () => void;
  onCancel: () => void;
  onContinue?: () => void;
  subscription?: PlanSubscriptionDetails;
  isOnboarding: boolean;
}> = ({
  product,
  onSubscribe,
  onCancel,
  onContinue,
  subscription,
  isOnboarding,
}) => {
  const { t } = useTranslation();
  const getPlanImage = (name: string) => {
    if (name.includes('personal_basic')) {
      return 'section-symbol-yellow.png';
    } else if (name.includes('premium')) {
      return 'section-symbol-green.png';
    } else {
      return 'section-symbol-blue.png';
    }
  }

  return (
    <Stack alignItems="center" gap="20px" textAlign="center">
      <Image src={`${process.env.PUBLIC_URL}/images/${getPlanImage(product.id)}`}/>
      <Typography
        fontSize="18px"
        fontWeight="600"
        lineHeight="1"
        color="primary"
      >
        {t(`subscription.plans.${product.id}.title`)}
      </Typography>

      <Stack alignItems="center" gap="8px">
        <Typography
          fontSize="14px"
          fontWeight="600"
          lineHeight="1"
          whiteSpace="pre"
        >
          {`${product.price.monthlyFormattedPrice} / ${t("subscription.plans.periodDuration.month")}`}
        </Typography>

        <Typography fontSize="12px" fontWeight="500" lineHeight="1">
          {t(`subscription.plans.billingPeriod.label`, {
            period: t(`subscription.plans.billingPeriod.${product.period}`),
          })}
        </Typography>

        {product.period === "year" && (
          <Typography fontSize="12px" fontWeight="500" lineHeight="1">
            {product.price.formattedPrice} /{" "}
            {t("subscription.plans.periodDuration.year")}
          </Typography>
        )}
      </Stack>

      <Stack gap="4px" width="100%">
        {!subscription?.plan || subscription.isTrial ? (
          <Button fullWidth variant="contained" onClick={onSubscribe}>
            {t("subscription.plans.subscribeButton")}
          </Button>
        ) : subscription.plan.name === product.id && !subscription.isTrial ? (
          <Button fullWidth variant="outlined" color="error" onClick={onCancel}>
            {t("subscription.plans.cancelButton")}
          </Button>
        ) : null}
        {isOnboarding &&
          onContinue &&
          subscription?.isTrial &&
          subscription.plan.name === product.id && (
            <>
              <Typography fontSize="14px" textTransform="uppercase">
                {t("or")}
              </Typography>
              <Button
                fullWidth
                variant="contained"
                onClick={onContinue}
                sx={{ whiteSpace: "nowrap" }}
              >
                {t("subscription.plans.continueTrialButton")}
              </Button>
            </>
          )}
      </Stack>
    </Stack>
  );
};

const HeaderText: FC<PropsWithChildren<any>> = ({ children }) => (
  <Typography fontSize="14px" color="grey.700">
    {children}
  </Typography>
);

const StyledTableRow: FC<TableRowProps> = (props) => {
  return (
    <TableRow
      {...props}
      sx={{
        "&:nth-of-type(even)": {
          backgroundColor: "grey.50",
        },
      }}
    />
  );
};

const StyledTableCell: FC<
  TableCellProps & { highlight: boolean; freeTrial?: boolean; first?: boolean; last?: boolean }
> = ({ highlight, freeTrial = false, first = false, last = false, ...props }) => {
  return (
    <TableCell
      {...props}
      sx={(theme) => ({
        ...(props.sx as any),
        borderLeft: highlight || freeTrial ? "solid 2px" : null,
        borderRight: highlight || freeTrial? "solid 2px" : null,
        borderColor: highlight || freeTrial? "primary.main" : null,
        ...(first &&
        (highlight || freeTrial) && {
            backgroundColor: highlight ? "primary.main" : "primary.contrastText",
            color: highlight ? "primary.contrastText" : "primary.main",
            borderTopLeftRadius: "10px",
            borderTopRightRadius: "10px",
            borderTop: `solid 2px ${theme.palette.primary.main} !important`,
            borderBottom: `solid 2px ${theme.palette.primary.main} !important`,
            //border: "none",
            padding: "8px",
          }),
        ...(last &&
          (highlight || freeTrial) && {
            borderBottomLeftRadius: "10px",
            borderBottomRightRadius: "10px",
            borderBottom: `solid 2px ${theme.palette.primary.main} !important`,
          }),
      })}
    />
  );
};

const SubscriptionItems: FC<{
  period: Period;
  products: {
    product: Product;
    plan: Plan;
  }[];
  onSubscribe: (product: Product) => void;
  onCancel: (product: Product) => void;
  onContinue?: () => void;
  onBillingPeriodChange: (period: Period) => void;
  subscription?: PlanSubscriptionDetails;
  isOnboarding: boolean;
}> = ({
  products,
  period,
  onSubscribe,
  onCancel,
  onContinue,
  onBillingPeriodChange,
  subscription,
  isOnboarding,
}) => {
  const { t } = useTranslation();

  return (
    <Stack overflow="auto" gap="24px" maxWidth="800px">
      <BillingPeriodSwitch value={period} onChange={onBillingPeriodChange} />
      <Table
        sx={{
          borderCollapse: "separate",
          [`& .${tableCellClasses.root}`]: {
            borderBottom: "none",
          },
        }}
      >
        <TableBody>
          <TableRow>
            <TableCell />
            {products.map(({ product, plan }) => (
              <Fragment key={product.id}>
                <StyledTableCell
                  component="th"
                  scope="row"
                  align="center"
                  highlight={plan.isHighlighted}
                  freeTrial={isFreeTrial(subscription, product)}
                  first={true}
                >
                  {plan.isHighlighted || isFreeTrial(subscription, product) ? (
                    <Typography fontSize="12px">
                      {t(
                        isFreeTrial(subscription, product)
                          ? `subscription.plans.freeTrial`
                          : `subscription.plans.popular`,
                      )}
                    </Typography>
                  ) : null}
                </StyledTableCell>
                <TableCell />
              </Fragment>
            ))}
          </TableRow>

          <TableRow>
            <TableCell component="th" scope="row" />

            {products.map(({ product, plan }) => (
              <Fragment key={product.id}>
                <StyledTableCell
                  highlight={plan.isHighlighted}
                  freeTrial={isFreeTrial(subscription, product)}
                  sx={{ minWidth: "160px", height: "200px" }}
                >
                  <SubscriptionItem
                    key={product.id}
                    product={product}
                    onSubscribe={() => onSubscribe(product)}
                    onCancel={() => onCancel(product)}
                    onContinue={onContinue}
                    subscription={subscription}
                    isOnboarding={isOnboarding}
                  />
                </StyledTableCell>
                <TableCell />
              </Fragment>
            ))}
          </TableRow>

          <StyledTableRow>
            <TableCell component="th" scope="row">
              <HeaderText>{t(`subscription.plans.minutes`)}</HeaderText>
            </TableCell>

            {products.map(({ product, plan }) => (
              <Fragment key={product.id}>
                <StyledTableCell
                  align="center"
                  highlight={plan.isHighlighted}
                  freeTrial={isFreeTrial(subscription, product)}
                >
                  <Limit
                    product={product}
                    subscription={subscription}
                    limit={plan.limits.talkSeconds/60}
                    remainingLimit={
                      subscription?.remainingLimits.talkSeconds == null
                        ? undefined
                        : subscription.remainingLimits.talkSeconds / 60
                    }
                  />
                </StyledTableCell>
                <TableCell />
              </Fragment>
            ))}
          </StyledTableRow>

          <StyledTableRow>
            <TableCell component="th" scope="row">
              <HeaderText>{t(`subscription.plans.pictures`)}</HeaderText>
            </TableCell>

            {products.map(({ product, plan }) => (
              <Fragment key={product.id}>
                <StyledTableCell
                  align="center"
                  highlight={plan.isHighlighted}
                  freeTrial={isFreeTrial(subscription, product)}
                >
                  <Limit
                    product={product}
                    subscription={subscription}
                    limit={plan.limits.photos}
                    remainingLimit={subscription?.remainingLimits.photos}
                  />
                </StyledTableCell>
                <TableCell />
              </Fragment>
            ))}
          </StyledTableRow>

          <StyledTableRow>
            <TableCell component="th" scope="row">
              <HeaderText>{t(`subscription.plans.events`)}</HeaderText>
            </TableCell>

            {products.map(({ product, plan }) => (
              <Fragment key={product.id}>
                <StyledTableCell
                  align="center"
                  highlight={plan.isHighlighted}
                  freeTrial={isFreeTrial(subscription, product)}
                  last={true}
                >
                  <Limit
                    product={product}
                    subscription={subscription}
                    limit={plan.limits.reminders}
                    remainingLimit={subscription?.remainingLimits.reminders}
                  />
                </StyledTableCell>
                <TableCell />
              </Fragment>
            ))}
          </StyledTableRow>
        </TableBody>
      </Table>
    </Stack>
  );
};

const Limit: FC<{
  product: Product;
  subscription?: PlanSubscriptionDetails;
  limit?: number;
  remainingLimit?: number | null;
}> = ({product, subscription, limit, remainingLimit}) => {

  const { t } = useTranslation();
  const formatLimit = (limit?: number) => {
    return limit && limit > 0 ? limit : t(`subscription.plans.unlimited`);
  };

  return (
    <>
      {formatLimit(limit)}
      {subscription?.plan.name === product.id && limit != null && limit > 0 && remainingLimit != null && remainingLimit <= 0 && (
        <>
          &nbsp;
          <Tooltip title={t('subscription.limitReachedAlert.message')}>
            <InfoOutlinedIcon color="error" fontSize="small" style={{verticalAlign: "bottom"}}/>
          </Tooltip>
        </>
      )}
    </>
  );
}

const DEFAULT_SUB_POLLING_INTERVAL = 60 * 1000; // 1 minute

const SubscriptionSelect: FC<{
  onSubscribed?: () => void;
  onContinue?: () => void;
  isOnboarding?: boolean;
}> = ({ onSubscribed, onContinue, isOnboarding = false }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [isCanceling, setIsCanceling] = useState(false);
  const email = useSelector((state: RootState) => state.auth.user?.email);
  const { initialized } = useSelector((state: RootState) => state.subscription);
  const [service] = useState<SubscriptionService>(
    RevenuecatSubscriptionService.instance,
  );
  const { data: plans } = useGetPlansQuery();
  const selectedPatientId = useSelector(
    (state: RootState) => state.home.selectedPatientId,
  );
  const [subPollingInterval, setSubPollingInterval] = useState<number>(
    DEFAULT_SUB_POLLING_INTERVAL,
  );
  const { data: subscription, refetch } = useGetPlanSubscriptionDetailsQuery(
    {
      patientId: selectedPatientId!,
    },
    { skip: !selectedPatientId, pollingInterval: subPollingInterval },
  );
  const [products, setProducts] = useState<Product[]>([]);
  const [period, setPeriod] = useState<Period>("month");

  useEffect(() => {
    if (!subscription) {
      return;
    }
    if (isExpired(subscription)) {
      setSubPollingInterval(DEFAULT_SUB_POLLING_INTERVAL);
      setIsCanceling(false);
      setConfirmDialogOpen(false);
    }
  }, [subscription]);

  const filteredProducts = useMemo(() => {
    if (!plans?.data?.length || !products?.length) {
      return [];
    }
    return products
      .filter((product) => product.period === period)
      .map((product) => {
        return {
          product,
          plan: plans.data.find((plan) => plan.name === product.id),
        };
      })
      .filter(({ plan }) => plan?.isActive) as {
      product: Product;
      plan: Plan;
    }[];
  }, [products, period, plans]);

  useEffect(() => {
    if (!initialized) {
      return;
    }
    (async () => {
      const products = await service.getProducts();
      setProducts(products);
    })();
  }, [service, initialized]);

  const subscribe = async (product: Product) => {
    if (await service.subscribe(product, email)) {
      refetch();
      onSubscribed?.();
    }
  };

  const cancelSubscription = async () => {
    setIsCanceling(true);
    try {
      await service.cancelSubscription();
      setSubPollingInterval(5_000);
    } catch (e) {
      dispatch(setMessage({ content: t(`errors.unknown`), severity: "error" }));
      setIsCanceling(false);
      setConfirmDialogOpen(false);
    }
  };

  return (
    <>
      {!initialized ? (
        <Loader />
      ) : (
        <>
          <SubscriptionItems
            period={period}
            products={filteredProducts}
            onSubscribe={subscribe}
            onCancel={() => setConfirmDialogOpen(true)}
            onContinue={onContinue}
            onBillingPeriodChange={(period) => setPeriod(period)}
            subscription={subscription}
            isOnboarding={isOnboarding}
          />
          <ConfirmDialog
            processing={isCanceling}
            open={confirmDialogOpen}
            onCancel={() => setConfirmDialogOpen(false)}
            onConfirm={cancelSubscription}
            title={t("subscription.cancelDialog.title")}
            content={t("subscription.cancelDialog.content", {subscription: t(`subscription.plans.${subscription?.plan.name}.title`)})}
          />
        </>
      )}
    </>
  );
};

export default SubscriptionSelect;
